import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Table,
  Spin,
  Typography,
  message,
  FloatButton,
  Tooltip,
  Tour,
  Statistic,
} from "antd";
import type { TourProps } from "antd";
import {
  ArrowUpOutlined,
  ClockCircleOutlined,
  LikeOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { get } from "../../Api/http/httpClient";
import dayjs from "dayjs";
import "./Analytics.css";
import ReactApexChart from "react-apexcharts";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface AnalyticsProps {
  user_data: any;
}

const Analytics: React.FC<AnalyticsProps> = ({
  user_data,
}) => {
  const [loading, setLoading] = useState(true);
  const [topicsByWeek, setTopicsByWeek] = useState<any[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any[]>([]);
  const [selectedDates, setSelectedDates] = useState<[Date, Date]>([
    dayjs().subtract(7, "days").toDate(),
    dayjs().toDate(),
  ]);
  const [averageResponseTime, setAverageResponseTime] = useState<number | null>(null);
  const [totalInteractions, setTotalInteractions] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  });
  const [open, setOpen] = useState<boolean>(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);

  const steps: TourProps["steps"] = [
    {
      title: "¿Cómo interpretar estos datos?",
      description:
        "Esta sección te presenta información clave sobre las interacciones con tus clientes mediante WhatsApp. Utiliza esta información para ajustar tu estrategia de atención al cliente, descubrir tendencias y mejorar la experiencia general de tus usuarios.",
      target: null,
    },
    {
      title: "Generación Automática de Datos",
      description:
        "Cada Día, entre las 5 y 6 de la mañana, se genera automáticamente un informe que recopila y procesa estas métricas. Esto asegura que siempre tengas datos actualizados para analizar y tomar decisiones informadas.",
      target: ref1.current,
    },
    {
      title: "Total de Interacciones",
      description:
        "Muestra cuántos mensajes entrantes se han intercambiado durante el periodo seleccionado.",
      target: ref2.current,
    },
    {
      title: "Interacciones por Usuario",
      description:
        "Muestra una tabla para visualizar las interacciones por cliente en el periodo establecido.",
      target: ref3.current,
    },
    {
      title: "Distribución de Temas",
      description:
        "Un gráfico que resalta los temas más frecuentes en las conversaciones, separados por semana, permitiéndote comprender las necesidades más comunes de tus clientes.",
      target: ref4.current,
    },
  ];

  const fetchAnalyticsData = async (startDate: Date, endDate: Date) => {
    setLoading(true);
    try {
      const url = `/analytics?establishmentID=${
        user_data["custom:establishment_id"]
      }&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      const response = await get(url);
  
      if (response.answer) {
        const items = response.items || [];
        let totalInteractions = 0;
        let totalResponseTime = 0;
        let recordCount = 0;
  
        const userInteractionsMap: Record<string, number> = {};
        let topicsByWeek: any[] = [];
  
        items.forEach((item: any) => {
          // Sumar las interacciones totales
          totalInteractions += item.total_interactions || 0;
  
          // Acumular el tiempo promedio de respuesta
          if (item.average_response_time) {
            totalResponseTime += parseFloat(item.average_response_time);
            recordCount += 1;
          }
  
          // Parsear los mensajes por usuario y acumular en el diccionario
          const parsedMessages = safeJsonParse(item.messages_per_user || "[]");
          parsedMessages.forEach((user: any) => {
            const chatUuid = user.user;
            userInteractionsMap[chatUuid] =
              (userInteractionsMap[chatUuid] || 0) + user.count;
          });
  
          // Parsear los topics y agregarlos tal cual
          const parsedTopics = safeJsonParse(item.topics || "{}");
          topicsByWeek.push({
            week: dayjs(item.day_date).format("YYYY-MM-DD"),
            topics: Object.entries(parsedTopics).map(([topic, details]: any) => ({
              type: topic,
              value: details.percentage,
            })),
          });
        });
  
        // Calcular el promedio del tiempo de respuesta
        const calculatedAverageResponseTime =
          recordCount > 0 ? totalResponseTime / recordCount : null;
  
        // Convertir el map de usuarios en un arreglo
        const consolidatedUsers = Object.entries(userInteractionsMap).map(
          ([chat_uuid, interactions]) => ({
            chat_uuid,
            interactions,
          })
        );
  
        // Actualizar estados
        setTotalInteractions(totalInteractions);
        setAverageResponseTime(calculatedAverageResponseTime);
        setAnalyticsData(consolidatedUsers);
        setTopicsByWeek(topicsByWeek);
      } else {
        setAnalyticsData([]);
        setTopicsByWeek([]);
        setTotalInteractions(0);
        setAverageResponseTime(null);
        message.warning("No se encontraron datos para el rango seleccionado.");
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      message.error("Error al obtener datos analíticos.");
    } finally {
      setLoading(false);
    }
  };
  

  const safeJsonParse = (data: string): any => {
    try {
      const validJson = data.replace(/'/g, '"').trim();
      return JSON.parse(validJson);
    } catch (error) {
      console.error("Error parsing JSON:", error, "Data:", data);
      return [];
    }
  };

  useEffect(() => {
    // Al montar el componente, establecer el rango de fechas de la última semana con hora 05:00:00.000Z
    const endDate = dayjs().hour(0).minute(0).second(0).millisecond(0).toDate();
    const startDate = dayjs()
      .subtract(7, "days")
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate();
    setSelectedDates([startDate, endDate]);
    fetchAnalyticsData(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      const startDate = dates[0].toDate();
      const endDate = dates[1].toDate();
      setSelectedDates([startDate, endDate]);
      fetchAnalyticsData(startDate, endDate);
    }
  };

  const handleTableChange = (paginationConfig: any) => {
    setPagination(paginationConfig);
  };

  const columns = [
    {
      title: "# Teléfono",
      dataIndex: "chat_uuid",
      key: "chat_uuid",
    },
    {
      title: "Interacciones",
      dataIndex: "interactions",
      key: "interactions",
    },
  ];

  const generateChartOptions = (weekData: any) => {
    const labels = weekData.topics.map((t: { type: string }) => t.type);
    const series = weekData.topics.map((t: { value: number }) => t.value);

    return {
      chart: {
        type: "bar" as "bar",
        height: 350,
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      series: [
        {
          data: series,
        },
      ],
      xaxis: {
        categories: labels,
        title: {
          text: "Porcentaje",
        },
      },
      yaxis: {
        title: {
          text: "Temas",
        },
      },
      colors: ["#0e6eaf"],
      tooltip: {
        y: {
          formatter: (val: number) => {
            return `${val}%`;
          },
        },
      },
    };
  };

  return loading ? (
    <Spin fullscreen />
  ) : (
    <>
      <div className="layout-content">
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="mb-24">
            <Card
              ref={ref1}
              className="criclebox h-full"
              bordered={false}
              title="Seleccionar Rango de Fechas"
            >
              <RangePicker
                value={[dayjs(selectedDates[0]), dayjs(selectedDates[1])]}
                onChange={handleDateChange}
                size="large"
                style={{ width: "100%" }}
              />
            </Card>
          </Col>
          <Col xs={12} xl={4} className="mb-24">
            <Card ref={ref2} bordered={false} className="criclebox h-full">
              <Statistic
                title="Total de Interacciones"
                value={totalInteractions}
                prefix={<LikeOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} xl={4} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <Statistic
                title="Tiempo Promedio de Respuesta"
                value={
                  averageResponseTime !== null
                    ? `${averageResponseTime.toFixed(2)} seg`
                    : "N/A"
                }
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} xl={4} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <Statistic
                title="Tasa de Resolución"
                value={11.28}
                precision={2}
                suffix="%"
                prefix={<ArrowUpOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={8} className="mb-24">
            <Card
              ref={ref3}
              bordered={false}
              className="criclebox h-full"
              title="Interacciones por Usuario"
            >
              <Table
                dataSource={analyticsData}
                columns={columns}
                rowKey="chat_uuid"
                pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "30"],
                }}
                onChange={handleTableChange}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={16} className="mb-24">
            {topicsByWeek.map((weekData, index) => (
              <Row gutter={[24, 0]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={24}>
                  <Card
                    key={index}
                    ref={ref4}
                    bordered={false}
                    className="criclebox h-full"
                    title={`Distribución de Temas - Fecha ${weekData.week}`}
                  >
                    <ReactApexChart
                      options={generateChartOptions(weekData)}
                      series={generateChartOptions(weekData).series}
                      type="bar"
                      height={250}
                    />
                  </Card>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>
        <Tooltip title="¿Cómo interpretar estos datos?" placement="left">
          <FloatButton
            icon={<QuestionCircleOutlined />}
            type="primary"
            style={{ insetInlineEnd: 24 }}
            onClick={() => setOpen(true)}
          />
        </Tooltip>

        <Tour open={open} onClose={() => setOpen(false)} steps={steps} />
      </div>
    </>
  );
};

export default Analytics;